"use strict";
/**
 * Weekly Report V3 — Beat 2 "Your Skin This Week"
 *
 * Optional content that the v3 Skin Snapshot section adds on top of the v2
 * fields. New reports populate these; historical reports render via the
 * legacy v2 fallback path without them.
 *
 * Three sub-structures:
 *   1. GoalCheckIn — per-goal verdict (progressing / steady / needs_attention)
 *      with optional LLM summary narrative when 2-3 goals have mixed verdicts.
 *   2. ChangeListItem — curated "focus metrics" for the week. Powers both the
 *      face-scan carousel (1 slide per item) AND the "What Changed" list.
 *      Filled via a waterfall: mcid_change > acute_signal > goal_aligned >
 *      baseline_coverage. 2-4 items, never empty when the user has data.
 *   3. Per-item region data (score + direction) rides on each
 *      ChangeListItem, so the carousel slide for that metric has everything
 *      it needs to render without a separate latestScan field.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.ChangeListItemSchema = exports.ChangeListItemRegionSchema = exports.RegionDirectionEnum = exports.ChangeReasonEnum = exports.ChangeCategoryEnum = exports.GoalCheckInSchema = exports.GoalCheckInFocusAreaSchema = exports.GoalVerdictEnum = void 0;
const zod_1 = require("zod");
const enums_1 = require("../api/enums");
const metric_dashboard_1 = require("../api/metric_dashboard");
const skin_goals_1 = require("../api/skin_goals");
// ============================================================================
// GOAL CHECK-IN
// ============================================================================
/**
 * Per-goal verdict. Computed from supporting metrics' trends:
 *   - `progressing`   when ≥50% of the goal's metrics are improving
 *   - `needs_attention` when ≥50% are declining
 *   - `steady`        otherwise
 *
 * Maps to frontend dot color: green / yellow / blue.
 */
exports.GoalVerdictEnum = zod_1.z.enum([
    'progressing',
    'steady',
    'needs_attention',
]);
exports.GoalCheckInFocusAreaSchema = zod_1.z.object({
    goalId: zod_1.z.string(),
    /** Human-readable goal name. Stored Title Case in DB; frontend may
     *  lowercase when splicing into body prose. */
    goalName: zod_1.z.string(),
    verdict: exports.GoalVerdictEnum,
    /**
     * Verdict copy. ≤120 chars — accommodates the wireframe's single-goal
     * editorial voice (A1/A2 examples run ~85-100 chars). Multi-goal
     * layouts (A3/A4) need phrases closer to 40 chars to stay on one line;
     * that's a frontend layout concern enforced by truncation, not by this
     * schema.
     */
    verdictPhrase: zod_1.z.string().max(120),
    /** The metric tech names Stella used to compute this verdict. */
    supportingMetrics: zod_1.z.array(zod_1.z.enum(enums_1.techNameEnum)),
});
exports.GoalCheckInSchema = zod_1.z.object({
    focusAreas: zod_1.z.array(exports.GoalCheckInFocusAreaSchema).max(skin_goals_1.MAX_SKIN_GOALS),
    /**
     * Optional editorial narrative that ties mixed-verdict goals together.
     * LLM-generated, may be null. Typically present for 2-3 goals with
     * genuinely connected movement; absent for single-goal or fully-aligned
     * cases.
     */
    summaryNarrative: zod_1.z.string().nullable(),
    /**
     * True when the user changed their goals during this report period.
     * Frontend renders a small "goals changed this week" callout so the
     * user understands the verdict is fresh.
     */
    goalsChanged: zod_1.z.boolean(),
});
// ============================================================================
// CHANGE LIST ITEM (powers carousel + list)
// ============================================================================
/**
 * Visual category. Drives dot color and tag treatment on both the carousel
 * slide and the list row.
 *   - improving  → green,  `+N` tag
 *   - watch      → yellow, `Watch` tag
 *   - stable     → blue,   `Stable` tag
 *   - declining  → coral,  `Watch` tag (never "declining" — compliance)
 *   - baseline   → muted,  no tag (first_report or waterfall fallback)
 */
exports.ChangeCategoryEnum = zod_1.z.enum([
    'improving',
    'watch',
    'stable',
    'declining',
    'baseline',
]);
/**
 * Why this item is in the list. Used to:
 *   - drive the section header ("What Changed" / "What Stella Is Watching"
 *     / "Your First Reads")
 *   - gate whether an LLM-rephrased body is worth the token cost
 *     (mcid_change earns it, baseline_coverage uses a template)
 *   - keep selection logic traceable for debugging
 */
exports.ChangeReasonEnum = zod_1.z.enum([
    'mcid_change',
    'acute_signal',
    'goal_aligned',
    'baseline_coverage',
]);
/**
 * Per-region direction for the face-scan overlay. `baseline` is used when
 * there's no delta to compute a direction from (first_report, or when a
 * ChangeListItem is present for non-delta reasons like baseline_coverage).
 *
 * **Score direction convention**: Every metric in Stella follows the rule
 * **higher score = better skin appearance**. A positive delta always means
 * the score went up, which always means the skin reads better on that
 * metric. Derivation code (trend classifiers, delta-to-direction mappers)
 * must assume this convention; no per-metric inversion exists.
 *
 * Note: these enum VALUES ('improving' / 'declining') are internal API
 * contracts. User-facing prose should say "moving", "shifting", "trending"
 * per brand/Brand Voice.md — never the raw enum labels.
 */
exports.RegionDirectionEnum = zod_1.z.enum([
    'improving',
    'stable',
    'declining',
    'watch',
    'baseline',
]);
/**
 * Regional score for a single face area on a single metric. Rides on the
 * parent ChangeListItem so each carousel slide has everything it needs.
 */
exports.ChangeListItemRegionSchema = zod_1.z.object({
    area: zod_1.z.enum(enums_1.areaEnum),
    /** 0-100 score for this metric in this region. Always present. */
    score: zod_1.z.number(),
    /**
     * Week-over-week delta in score points. Null when no prior report exists
     * (first_report) or when the parent item was selected for a non-delta
     * reason (baseline_coverage). Frontend hides the delta pill when null.
     */
    delta: zod_1.z.number().nullable(),
    direction: exports.RegionDirectionEnum,
    /**
     * Face landmark coordinates for the area's centroid (MediaPipe 478
     * landmark indexed via AREA_LANDMARK_INDEX in MetricDomain). Drives
     * marker placement on the FaceScanCarousel overlay. Optional because
     * scans without face_landmarks data fall back to no overlay.
     *
     * `landmark` is the legacy single-point field, preserved for backward
     * compatibility with persisted reports written before bilateral overlay
     * support. New reports populate both fields; the renderer prefers
     * `landmarks` when present.
     */
    landmark: metric_dashboard_1.FaceLandmarkSchema.optional(),
    /**
     * Multiple face landmark anchors for the same regional score. Used when
     * dermatologist guidance places one metric on two anatomical points
     * (e.g., bilateral nasolabial folds, both jowls). When present, takes
     * precedence over `landmark` at the renderer. New writers populate this
     * with one or more points; old persisted rows have only `landmark`.
     */
    landmarks: metric_dashboard_1.FaceLandmarkSchema.array().optional(),
});
/**
 * One curated focus metric for the week. Each item powers:
 *   - one carousel slide in the face-scan overlay (via `regions`)
 *   - one row in the "What Changed" list below
 *
 * Items with different `reason` values coexist in the same list — mixed
 * cases are explicitly supported. The frontend renders each item based on
 * its OWN category + reason, not on sibling items.
 */
exports.ChangeListItemSchema = zod_1.z.object({
    category: exports.ChangeCategoryEnum,
    reason: exports.ChangeReasonEnum,
    /** Short headline. LLM-rephrased for mcid_change; template for the rest. */
    title: zod_1.z.string(),
    /** One-line read, ~1-2 sentences. */
    body: zod_1.z.string(),
    /**
     * Aggregate week-over-week delta (headline figure). Null for first_report
     * or non-delta reasons. Frontend hides any delta pill when null.
     */
    delta: zod_1.z.number().nullable(),
    metricTechName: zod_1.z.enum(enums_1.techNameEnum),
    metricDisplayName: zod_1.z.string(),
    /** Per-region breakdown for the carousel slide. */
    regions: zod_1.z.array(exports.ChangeListItemRegionSchema),
});
//# sourceMappingURL=skinThisWeek.js.map